import { BlockStack, Card, ChoiceList, Divider } from '@shopify/polaris';
import React from 'react';

import ColorPickerCustom from '@/components/ColorPickerCustom';
import { ActionCancel, RedirectUrl } from '@/constants/enum';
import { handleChangePreview, previewSelector } from '@/redux/slice/preview.slice';
import { TextField } from '@shopify/polaris';
import { useDispatch, useSelector } from 'react-redux';
import BoldText from '@/components/BoldText';
import { useTranslation } from 'react-i18next';
import EditorCustom from '@/components/EditorCustom';
import { cancelButtonInitial, submitButtonInitial } from '@/constants/options';
import { validatePositiveIntegerInput } from '@/helpers/string';
import { FontSize } from '@/components/EditorCustom/FontSizeButton';
import userPlans from '@/hooks/userPlans';
import CustomBadge from '@/components/CustomBadge';
import useLimitPlan from '@/hooks/useLimitPlan';

interface ButtonSettingsProps {}

const ButtonSettings: React.FC<ButtonSettingsProps> = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['popup', 'translation']);
  const previewDetail = useSelector(previewSelector);
  const { isUserFreeLimited, isUserBasicLimited, isUserFreeOrBasicLimited } = userPlans();
  const { isShowUpgradeNowRulePopup } = useLimitPlan();
  const handleChangeTemplate = (key: keyof typeof previewDetail) => (value: string) => {
    if (
      key === 'submitButtonBorderRadius' ||
      key === 'cancelButtonBorderRadius' ||
      key === 'submitButtonBorderWidth' ||
      key === 'cancelButtonBorderWidth'
    ) {
      value = validatePositiveIntegerInput(value);
    }

    dispatch(
      handleChangePreview({
        [key]: value,
      }),
    );
  };

  const renderChildrenRedirect = (isSelected: boolean) =>
    isSelected && (
      <TextField
        autoComplete=""
        label=""
        value={previewDetail.redirectUrl}
        onChange={handleChangeTemplate('redirectUrl')}
        onBlur={() => {
          if (!previewDetail.redirectUrl.trim().length) handleChangeTemplate('redirectUrl')(RedirectUrl.Google);
        }}
        disabled={isShowUpgradeNowRulePopup}
      />
    );

  const renderChildrenErrorMessageCancel = (isSelected: boolean) =>
    isSelected && (
      <TextField
        autoComplete=""
        label=""
        value={previewDetail?.errorMessageCancel || ''}
        onChange={handleChangeTemplate('errorMessageCancel')}
        placeholder={t('enter_error_message')}
        maxLength={255}
        showCharacterCount
        disabled={isShowUpgradeNowRulePopup}
      />
    );

  return (
    <BlockStack gap="200">
      <Card>
        <BlockStack gap="300">
          <BlockStack gap="100">
            <BoldText>
              {t('submit_button')} {isShowUpgradeNowRulePopup && <CustomBadge />}
            </BoldText>
            <EditorCustom
              value={previewDetail?.submitButtonLabelEditor ?? previewDetail?.submitButtonLabel ?? submitButtonInitial(t)}
              onChange={handleChangeTemplate('submitButtonLabelEditor')}
              defaultFontSize={FontSize.Base}
              disabledFontFamily={isUserFreeOrBasicLimited}
              disabledColor={isUserFreeLimited}
              disabledFontSize={isUserFreeLimited}
              disabledEditor={isShowUpgradeNowRulePopup}
            />
          </BlockStack>
        </BlockStack>
      </Card>

      <Card>
        <BoldText>
          {t('translation:submit_button_action')} {isShowUpgradeNowRulePopup && <CustomBadge />}
        </BoldText>
        <div className="mt-8 mb-8">
          <Divider />
        </div>
        <TextField
          autoComplete=""
          label={t('error_message')}
          value={previewDetail?.errorMessage || ''}
          onChange={handleChangeTemplate('errorMessage')}
          placeholder={t('enter_error_message')}
          maxLength={255}
          showCharacterCount
          disabled={isShowUpgradeNowRulePopup}
        />
      </Card>

      <Card>
        <BoldText>{t('button_style')}</BoldText>
        <div className="mt-8 mb-8">
          <Divider />
        </div>
        <BlockStack gap="500">
          <ColorPickerCustom
            onChange={handleChangeTemplate('submitButtonBackground')}
            label={t('background')}
            color={previewDetail.submitButtonBackground}
            disabled={isUserFreeLimited || isShowUpgradeNowRulePopup}
            showBadge
          />

          <ColorPickerCustom
            onChange={handleChangeTemplate('submitButtonBorderColor')}
            label={t('border_color')}
            color={previewDetail.submitButtonBorderColor}
            disabled={isUserFreeOrBasicLimited}
            showBadge
          />

          <TextField
            autoComplete=""
            label={
              <>
                {t('border_radius')} {isUserFreeOrBasicLimited && <CustomBadge />}
              </>
            }
            value={previewDetail.submitButtonBorderRadius}
            onChange={handleChangeTemplate('submitButtonBorderRadius')}
            suffix="px"
            disabled={isUserFreeOrBasicLimited}
          />

          <TextField
            autoComplete=""
            label={
              <>
                {t('border_width')} {isUserFreeOrBasicLimited && <CustomBadge />}
              </>
            }
            value={previewDetail.submitButtonBorderWidth}
            onChange={handleChangeTemplate('submitButtonBorderWidth')}
            suffix="px"
            disabled={isUserFreeOrBasicLimited}
          />
        </BlockStack>
      </Card>

      <Card>
        <BlockStack gap="300">
          <BlockStack gap="200">
            <BoldText>
              {t('cancel_button')} {isShowUpgradeNowRulePopup && <CustomBadge />}
            </BoldText>
            <EditorCustom
              value={previewDetail?.cancelButtonLabelEditor ?? previewDetail?.cancelButtonLabel ?? cancelButtonInitial(t)}
              onChange={handleChangeTemplate('cancelButtonLabelEditor')}
              defaultFontSize={FontSize.Base}
              disabledFontFamily={isUserFreeLimited || isUserBasicLimited}
              disabledColor={isUserFreeLimited}
              disabledFontSize={isUserFreeLimited}
              disabledEditor={isShowUpgradeNowRulePopup}
            />
          </BlockStack>
        </BlockStack>
      </Card>

      <Card>
        <BoldText>
          {t('translation:cancel_button_action')} {isShowUpgradeNowRulePopup && <CustomBadge />}
        </BoldText>
        <div className="mt-8 mb-8">
          <Divider />
        </div>
        <ChoiceList
          title={t('visitor_clicks_cancel')}
          choices={[
            {
              label: t('redirect_url'),
              value: ActionCancel.REDIRECT,
              renderChildren: renderChildrenRedirect,
            },
            {
              label: t('show_error_message'),
              value: ActionCancel.SHOW_ERROR,
              renderChildren: renderChildrenErrorMessageCancel,
            },
          ]}
          selected={[previewDetail.actionCancel]}
          onChange={(value) => handleChangeTemplate('actionCancel')(value[0])}
          disabled={isShowUpgradeNowRulePopup}
        />
      </Card>

      <Card>
        <BoldText>{t('button_style')}</BoldText>
        <div className="mt-8 mb-8">
          <Divider />
        </div>
        <BlockStack gap="500">
          <ColorPickerCustom
            onChange={handleChangeTemplate('cancelButtonBackground')}
            label={t('background')}
            color={previewDetail.cancelButtonBackground}
            disabled={isUserFreeLimited || isShowUpgradeNowRulePopup}
            showBadge
          />

          <ColorPickerCustom
            onChange={handleChangeTemplate('cancelButtonBorderColor')}
            label={t('border_color')}
            color={previewDetail.cancelButtonBorderColor}
            disabled={isUserFreeOrBasicLimited}
            showBadge
          />

          <TextField
            autoComplete=""
            label={
              <>
                {t('border_radius')} {isUserFreeOrBasicLimited && <CustomBadge />}
              </>
            }
            value={previewDetail.cancelButtonBorderRadius}
            onChange={handleChangeTemplate('cancelButtonBorderRadius')}
            suffix="px"
            disabled={isUserFreeOrBasicLimited}
          />

          <TextField
            autoComplete=""
            label={
              <>
                {t('border_width')} {isUserFreeOrBasicLimited && <CustomBadge />}
              </>
            }
            value={previewDetail.cancelButtonBorderWidth}
            onChange={handleChangeTemplate('cancelButtonBorderWidth')}
            suffix="px"
            disabled={isUserFreeOrBasicLimited}
          />
        </BlockStack>
      </Card>
    </BlockStack>
  );
};

export default ButtonSettings;
